import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { doc, getDoc, setDoc, onSnapshot, query, where, collection } from "firebase/firestore";
import Header from "../../components/Header/Header";
import logoRound from "../../assets/images/logo_round.png";

interface Result {
  time: string;
  number: number | null;
}

interface LotteryType {
  id: string;
  name: string;
}

// ✅ Convert 24h → AM/PM
const formatToAMPM = (time24: string) => {
  const [hourStr, minute] = time24.split(":");
  let hour = parseInt(hourStr);

  const ampm = hour >= 12 ? "PM" : "AM";

  hour = hour % 12;
  if (hour === 0) hour = 12;

  return `${hour}:${minute} ${ampm}`;
};

// ✅ Sort helper
const sortTimes = (a: string, b: string) => {
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);

  return ah * 60 + am - (bh * 60 + bm);
};

const Home: React.FC = () => {
  const [lotteries, setLotteries] = useState<LotteryType[]>([]);
  const [selectedLottery, setSelectedLottery] = useState<string>("");
  const [todayResults, setTodayResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState<Result | null>(null);

  const todayDate = new Date();
  const formattedDate = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, "0")}-${String(todayDate.getDate()).padStart(2, "0")}`;

  // 1. Fetch Active Lottery Names
  useEffect(() => {
    const q = query(collection(db, "lotteryNames"), where("isActive", "==", true));
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, name: d.data().name }));
      setLotteries(list);

      if (list.length > 0) {
        // Use functional update to check 'current' selection without dependency
        setSelectedLottery((current) => {
          const isStillActive = list.some(l => l.id === current);
          if (!current || !isStillActive) return list[0].id;
          return current;
        });
      } else {
        // THIS IS THE INSTANT CLEANUP
        setSelectedLottery("");
        setTodayResults([]);
        setLatest(null);
        setLoading(false); // Stop loading so message can show
      }
    });

    return () => unsubscribe();
  }, []);

  // 2. Fetch Results for Selected Lottery
  useEffect(() => {
    if (!selectedLottery) return;

    setLoading(true);
    // Path: lotteryResults > [LotteryID] > dates > [YYYY-MM-DD]
    const ref = doc(db, "lotteryResults", selectedLottery, "dates", formattedDate);

    const initializeAndListen = async () => {
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          date: formattedDate,
          slots: (() => {
            const s: any = {};
            // Loop from hour 9 (9 AM) up to 22 (10 PM)
            for (let h = 9; h <= 22; h++) {
              for (let m of ["00", "30"]) {
                // Logic to stop exactly at 22:00 and not include 22:30
                if (h === 22 && m === "30") break; 

                s[`${String(h).padStart(2, "0")}:${m}`] = { number: null };
              }
            }
            return s;
          })(),
          createdAt: new Date()
        });
      }

      return onSnapshot(ref, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const slotsObject = data.slots || {};
          const sortedTimes = Object.keys(slotsObject).sort(sortTimes);

          const slotsArray = sortedTimes.map((t) => ({
            time: formatToAMPM(t),
            number: slotsObject[t]?.number ?? null,
          }));

          setTodayResults(slotsArray);
          const updated = slotsArray.filter(s => s.number !== null);
          setLatest(updated.length > 0 ? updated[updated.length - 1] : null);
        }
        setLoading(false);
      });
    };

    let unsub: any;
    initializeAndListen().then(u => unsub = u);
    return () => unsub && unsub();
  }, [selectedLottery, formattedDate]);

  return (
    <div className="h-[100dvh] bg-gray-100 flex justify-center items-center p-2">

      {/* Big Main Card */}
      <div className="w-full max-w-3xl bg-[#fffcf9] border border-black rounded-2xl shadow-lg p-3 flex flex-col h-full max-h-[95vh] overflow-hidden">

        {/* <div className="flex justify-between items-center mb-2 shrink-0">
          <img src={logo} alt="Logo" className="h-10 w-auto object-contain" />
          <button 
            onClick={() => navigate("/history")}
            className="bg-black text-white text-[10px] font-bold px-3 py-1.5 rounded-full hover:bg-[#fa3815] transition"
          >
            History
          </button>
        </div> */}
        <Header
            isShowLogout={false} 
            buttonTitle="History"
            targetPath="/history"
        />

        {lotteries.length === 1 ? (
          /* Only one lottery: Show disabled input */
          <div>
            {/* <label className="text-xs font-bold text-black ml-2">LOTTERY NAME</label>
            <input
              type="text"
              value={lotteries[0].name}
              readOnly
              disabled
              className="w-full border rounded-full px-3 py-2 bg-gray-50 text-black cursor-not-allowed"
            /> */}
            <div className=" flex flex-col text-center mb-2 mt-2 shrink-0">
              <span className="text-[#fa3815] text-2xl font-bold">{lotteries[0].name}</span>
              <span className="text-black text-[14px] font-bold">{todayDate.toDateString()} (Today)</span>
            </div>
          </div>
        ) : (
          <>
            {lotteries.length > 0 &&
              <div className="text-center mb-2 mt-2 shrink-0">
                <select
                  value={selectedLottery}
                  onChange={(e) => setSelectedLottery(e.target.value)}
                  className="mb-2 w-full border-2 border-black rounded-full px-3 py-2 bg-white font-semibold"
                >
                  {lotteries.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <span className="text-black text-[14px] font-bold">{todayDate.toDateString()} (Today)</span>
              </div>
            }</>
        )}

        {latest && (
          <div className="flex flex-col items-center mb-3 shrink-0">

            <div className="popup-loop">
              {/* <div className={`w-24 h-24 rounded-full bg-gradient-to-br from-[#fa3815]/50 to-[#fa3815] flex flex-col items-center justify-center text-white font-semibold shadow-lg bg-cover bg-center blur-[1px]`} style={{ backgroundImage: `url(${logoRound})`}}>
                <div className="relative z-10 flex flex-col items-center justify-center text-white">
                  <span className="text-3xl text-black leading-none">{String(latest.number).padStart(2, "0")}</span>
                  <span className="text-[14px] font-semibold uppercase">{latest.time}</span>
                </div>
              </div> */}

              <div 
                className="relative w-24 h-24 rounded-full flex flex-col items-center justify-center text-white font-semibold shadow-lg overflow-hidden bg-cover bg-center"
                style={{ backgroundImage: `url(${logoRound})` }}
              >
                <div className="absolute inset-0 bg-black/60 z-0"></div>
                <span className="relative z-10 text-3xl text-white font-bold leading-none drop-shadow-[0_2px_1px_rgba(0,0,0,0.7)]">
                  {String(latest.number).padStart(2, "0")}
                </span>
                <span className="relative z-10 text-[12px] font-semibold uppercase text-white drop-shadow-[0_1px_0_rgba(0,0,0,0.7)]">
                  {latest.time}
                </span>
              </div>
              
            </div>

          </div>
        )}

        {/* Today Results */}
        {lotteries.length === 0 && !loading ? (
        /* NO ACTIVE LOTTERIES MESSAGE */
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
              <span className="text-2xl">🚫</span>
            </div>
            <h2 className="text-lg font-bold text-gray-800">No Active Lotteries</h2>
            <p className="text-gray-500 text-xs mt-1">Please check back later for new updates.</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col flex-1 min-h-0">
              <div className="grid grid-cols-3 px-2 py-1.5 font-semibold text-black text-[14px] uppercase tracking-wider shrink-0">
                <div className="ml-2">S.no</div>
                <div className="text-center">Time</div>
                <div className="text-right mr-2">Number</div>
              </div>

              {/* Scrollable List */}
              <div className="flex-1 overflow-y-auto space-y-2 no-scrollbar">

                {loading && (
                  <div className="text-center text-gray-500">Loading...</div>
                )}

                {!loading && todayResults?.map((item, index) => (
                  <div
                    key={index}
                    className="group grid grid-cols-3 items-center px-2 py-2 border rounded-full bg-white text-md shadow-sm transition hover:bg-[#fa3815] hover:text-white hover:shadow-md hover:border-none"
                  >
                    <div className="ml-2">
                      {index + 1}.
                    </div>

                    <div className="text-center">
                      {item.time}
                    </div>

                    <div className="font-medium text-right text-[#64d416] group-hover:text-white mr-2">
                      {item.number ? (
                        String(item.number).padStart(2, "0")
                      ) : (
                        <span className="text-[#fa3815] animate-pulse-custom group-hover:text-black text-sm">
                          waiting...
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Home;